import type {
  CompositionPage,
  CompositionPageOrientation,
  CompositionPagePreset,
  NormalizedPoint,
  NormalizedRect,
} from './model';
import { COMPOSITION_GRID_SPACING_MM } from './model';

export interface PageDimensionsMm {
  readonly width: number;
  readonly height: number;
}

export interface PreviewDimensions {
  readonly width: number;
  readonly height: number;
}

const PORTRAIT_PAGE_DIMENSIONS: Readonly<
  Record<Exclude<CompositionPagePreset, 'custom'>, PageDimensionsMm>
> = {
  a5: { width: 148, height: 210 },
  a4: { width: 210, height: 297 },
  a3: { width: 297, height: 420 },
  letter: { width: 215.9, height: 279.4 },
};

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

export function pageDimensionsMm(
  preset: Exclude<CompositionPagePreset, 'custom'>,
  orientation: CompositionPageOrientation,
): PageDimensionsMm {
  const portrait = PORTRAIT_PAGE_DIMENSIONS[preset];
  return orientation === 'portrait' ? portrait : { width: portrait.height, height: portrait.width };
}

export function compositionPageDimensionsMm(
  page: Pick<CompositionPage, 'preset' | 'orientation' | 'customWidthMm' | 'customHeightMm'>,
): PageDimensionsMm {
  if (page.preset === 'custom') {
    return { width: page.customWidthMm ?? 210, height: page.customHeightMm ?? 297 };
  }
  return pageDimensionsMm(page.preset, page.orientation);
}

export interface PageMarginsMm {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export function normalizedBoundsToMarginsMm(
  bounds: NormalizedRect,
  page: PageDimensionsMm,
): PageMarginsMm {
  return {
    top: bounds.y * page.height,
    right: (1 - bounds.x - bounds.width) * page.width,
    bottom: (1 - bounds.y - bounds.height) * page.height,
    left: bounds.x * page.width,
  };
}

export function marginsMmToNormalizedBounds(
  margins: PageMarginsMm,
  page: PageDimensionsMm,
  minimumSize = 0.05,
): NormalizedRect | null {
  if (
    !Object.values(margins).every((value) => Number.isFinite(value) && value >= 0) ||
    !Number.isFinite(page.width) ||
    !Number.isFinite(page.height)
  ) {
    return null;
  }
  const width = 1 - (margins.left + margins.right) / page.width;
  const height = 1 - (margins.top + margins.bottom) / page.height;
  if (width < minimumSize || height < minimumSize) return null;
  return {
    x: margins.left / page.width,
    y: margins.top / page.height,
    width,
    height,
  };
}

export const FILL_PAGE_BOUNDS: NormalizedRect = { x: 0, y: 0, width: 1, height: 1 };

const snapValue = (value: number, targets: readonly number[], tolerance: number) => {
  let nearest = value;
  let distance = tolerance + Number.EPSILON;
  for (const target of targets) {
    const candidateDistance = Math.abs(value - target);
    if (candidateDistance <= tolerance && candidateDistance < distance) {
      nearest = target;
      distance = candidateDistance;
    }
  }
  return nearest;
};

export function normalizedSnapTargets(sizeMm: number, gridSpacingMm: number): readonly number[] {
  const targets = [0, 0.5, 1];
  for (let mm = gridSpacingMm; mm < sizeMm; mm += gridSpacingMm) {
    targets.push(mm / sizeMm);
  }
  return targets;
}

export function snapNormalizedPoint(
  point: NormalizedPoint,
  page: PageDimensionsMm,
  tolerance: NormalizedPoint,
  gridSpacingMm = COMPOSITION_GRID_SPACING_MM,
  maximum = 0.98,
): NormalizedPoint {
  const snapped = {
    x: snapValue(point.x, normalizedSnapTargets(page.width, gridSpacingMm), tolerance.x),
    y: snapValue(point.y, normalizedSnapTargets(page.height, gridSpacingMm), tolerance.y),
  };
  return { x: clamp(snapped.x, 0, maximum), y: clamp(snapped.y, 0, maximum) };
}

export function snapMovedRect(
  rect: NormalizedRect,
  page: PageDimensionsMm,
  tolerance: NormalizedPoint,
  gridSpacingMm = COMPOSITION_GRID_SPACING_MM,
): NormalizedRect {
  const targetsX = normalizedSnapTargets(page.width, gridSpacingMm);
  const targetsY = normalizedSnapTargets(page.height, gridSpacingMm);
  const left = snapValue(rect.x, targetsX, tolerance.x);
  const right = snapValue(rect.x + rect.width, targetsX, tolerance.x);
  const centreX = snapValue(rect.x + rect.width / 2, targetsX, tolerance.x);
  const top = snapValue(rect.y, targetsY, tolerance.y);
  const bottom = snapValue(rect.y + rect.height, targetsY, tolerance.y);
  const centreY = snapValue(rect.y + rect.height / 2, targetsY, tolerance.y);
  const xCandidates = [left, right - rect.width, centreX - rect.width / 2];
  const yCandidates = [top, bottom - rect.height, centreY - rect.height / 2];
  const x = xCandidates.reduce((best, candidate) =>
    Math.abs(candidate - rect.x) < Math.abs(best - rect.x) ? candidate : best,
  );
  const y = yCandidates.reduce((best, candidate) =>
    Math.abs(candidate - rect.y) < Math.abs(best - rect.y) ? candidate : best,
  );
  return { ...rect, x: clamp(x, 0, 1 - rect.width), y: clamp(y, 0, 1 - rect.height) };
}

export function snapResizedRect(
  rect: NormalizedRect,
  corner: ResizeHandle,
  page: PageDimensionsMm,
  tolerance: NormalizedPoint,
  minimumSize = 0.05,
  gridSpacingMm = COMPOSITION_GRID_SPACING_MM,
): NormalizedRect {
  const left = corner.includes('west')
    ? snapValue(rect.x, normalizedSnapTargets(page.width, gridSpacingMm), tolerance.x)
    : rect.x;
  const right = corner.includes('east')
    ? snapValue(rect.x + rect.width, normalizedSnapTargets(page.width, gridSpacingMm), tolerance.x)
    : rect.x + rect.width;
  const top = corner.includes('north')
    ? snapValue(rect.y, normalizedSnapTargets(page.height, gridSpacingMm), tolerance.y)
    : rect.y;
  const bottom = corner.includes('south')
    ? snapValue(
        rect.y + rect.height,
        normalizedSnapTargets(page.height, gridSpacingMm),
        tolerance.y,
      )
    : rect.y + rect.height;
  if (right - left < minimumSize || bottom - top < minimumSize) return rect;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function fitPagePreview(
  available: PreviewDimensions,
  page: PageDimensionsMm,
  padding = 48,
): PreviewDimensions & { readonly pixelsPerMm: number } {
  const usableWidth = Math.max(1, available.width - padding * 2);
  const usableHeight = Math.max(1, available.height - padding * 2);
  const pixelsPerMm = Math.min(usableWidth / page.width, usableHeight / page.height);
  return {
    width: page.width * pixelsPerMm,
    height: page.height * pixelsPerMm,
    pixelsPerMm,
  };
}

export function normalizedRectToPreview(
  rect: NormalizedRect,
  preview: PreviewDimensions,
): NormalizedRect {
  return {
    x: rect.x * preview.width,
    y: rect.y * preview.height,
    width: rect.width * preview.width,
    height: rect.height * preview.height,
  };
}

export function normalizedPointToMm(
  point: NormalizedPoint,
  page: PageDimensionsMm,
): NormalizedPoint {
  return { x: point.x * page.width, y: point.y * page.height };
}

export function normalizedRectToMm(rect: NormalizedRect, page: PageDimensionsMm): NormalizedRect {
  return {
    x: rect.x * page.width,
    y: rect.y * page.height,
    width: rect.width * page.width,
    height: rect.height * page.height,
  };
}

export function clampNormalizedPoint(point: NormalizedPoint): NormalizedPoint {
  return { x: clamp(point.x, 0, 0.98), y: clamp(point.y, 0, 0.98) };
}

export function moveNormalizedRect(rect: NormalizedRect, delta: NormalizedPoint): NormalizedRect {
  return {
    ...rect,
    x: clamp(rect.x + delta.x, 0, 1 - rect.width),
    y: clamp(rect.y + delta.y, 0, 1 - rect.height),
  };
}

export type ResizeCorner = 'north-east' | 'north-west' | 'south-east' | 'south-west';
export type ResizeHandle = ResizeCorner | 'north' | 'east' | 'south' | 'west';

export function resizeNormalizedRect(
  rect: NormalizedRect,
  corner: ResizeHandle,
  delta: NormalizedPoint,
  minimumSize = 0.1,
): NormalizedRect {
  let left = rect.x;
  let top = rect.y;
  let right = rect.x + rect.width;
  let bottom = rect.y + rect.height;

  if (corner.includes('west')) left = clamp(left + delta.x, 0, right - minimumSize);
  if (corner.includes('east')) right = clamp(right + delta.x, left + minimumSize, 1);
  if (corner.includes('north')) top = clamp(top + delta.y, 0, bottom - minimumSize);
  if (corner.includes('south')) bottom = clamp(bottom + delta.y, top + minimumSize, 1);

  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Resizes from one corner while retaining the authored normalized aspect ratio. */
export function resizeNormalizedRectWithAspect(
  rect: NormalizedRect,
  corner: ResizeHandle,
  delta: NormalizedPoint,
  minimumSize = 0.01,
  aspectRatio = rect.width / rect.height,
): NormalizedRect {
  const fixedX = corner.includes('west') ? rect.x + rect.width : rect.x;
  const fixedY = corner.includes('north') ? rect.y + rect.height : rect.y;
  const movingX = (corner.includes('west') ? rect.x : rect.x + rect.width) + delta.x;
  const movingY = (corner.includes('north') ? rect.y : rect.y + rect.height) + delta.y;
  const rawWidth = Math.abs(movingX - fixedX);
  const rawHeight = Math.abs(movingY - fixedY);
  const ratio = aspectRatio;
  let height = (ratio * rawWidth + rawHeight) / (ratio * ratio + 1);
  let width = ratio * height;
  const maximumWidth = corner.includes('west') ? fixedX : 1 - fixedX;
  const maximumHeight = corner.includes('north') ? fixedY : 1 - fixedY;
  const boundedScale = Math.min(1, maximumWidth / width, maximumHeight / height);
  width *= boundedScale;
  height *= boundedScale;
  if (width < minimumSize || height < minimumSize) return rect;
  return {
    x: corner.includes('west') ? fixedX - width : fixedX,
    y: corner.includes('north') ? fixedY - height : fixedY,
    width,
    height,
  };
}
